import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

// ─────────────────────────────────────────────────────────────────────
// WIRING A LABEL TO THE CONTROL IT DESCRIBES (#781 slice 10).
//
// `Field` and `LockedField` both render a `<label>` above arbitrary children.
// Both rendered it as a SIBLING with no `htmlFor`, so the primitive meant to
// solve labelling labelled nothing: ~294 of the app's 417 form controls have no
// programmatic label, and these two are where that is supposed to be fixed.
//
// The wiring lives here rather than in either component because they need the
// same three decisions, and a second copy of this would drift.
// ─────────────────────────────────────────────────────────────────────

/** The attributes a labelled control needs, as a control itself receives them. */
export interface ControlAttrs {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
}

/**
 * A DOM id derived from `useId`, with React's punctuation removed.
 *
 * React 18's `useId` returns `:r0:`. That is a legal HTML id and `htmlFor`
 * resolves it, but it is NOT a legal CSS identifier — anything that later
 * reaches for `querySelector('#' + id)` breaks on it, silently and far from
 * here. Stripping the colons keeps the uniqueness (the `r0` part is what is
 * unique) and loses the trap.
 */
export const safeId = (prefix: string, auto: string): string =>
  `${prefix}-${auto.replace(/[^a-zA-Z0-9_-]/g, '')}`;

/** What `wireControl` managed to do — the label may only claim `htmlFor` if it worked. */
export interface Wired {
  /** The children, with the control attributes injected where possible. */
  node: ReactNode;
  /**
   * The id the `<label>` should point at, or `undefined` when the children
   * could not be wired.
   *
   * A `htmlFor` pointing at an id that does not exist is not a label — it is a
   * label-shaped lie, which is worse than the honest absence we started with,
   * because the next person reads the markup and believes the field is
   * labelled. So the caller omits `htmlFor` entirely in that case.
   */
  forId: string | undefined;
}

/**
 * Inject `id` / `aria-describedby` / `aria-invalid` into a single-element child.
 *
 * WHAT IT CAN AND CANNOT DO, said plainly:
 *
 *   • ONE element child (`<Field label="…"><input/></Field>`) — wired. This is
 *     every current call site but one.
 *   • A child that already carries its own `id` — left alone, and the label
 *     points at that id instead. A caller who set an id meant it.
 *   • A fragment, several children, or bare text — NOT wired, because there is
 *     no single control to point at and guessing which of three inputs the
 *     label means would be worse than saying nothing. Those call sites pass an
 *     explicit `htmlFor`, or use the render-prop form, and that is the migration
 *     follow-up rather than something this primitive can decide.
 *   • A COMPONENT child that drops the props it is handed — accepted here and
 *     invisible: the clone succeeds, nothing reaches the DOM, and the `htmlFor`
 *     dangles. That is why `NumInput`, `NumStepper` and `SetupNumberInput`
 *     declare these three props as part of this slice; a component that does
 *     not forward them has to be fixed where it lives.
 */
export function wireControl(children: ReactNode, control: ControlAttrs): Wired {
  if (!isValidElement(children)) return { node: children, forId: undefined };
  const el = children as ReactElement<Record<string, unknown>>;
  const existing = el.props ?? {};
  const own = typeof existing.id === 'string' ? existing.id : undefined;
  return {
    node: cloneElement(el, {
      id: own ?? control.id,
      'aria-describedby': existing['aria-describedby'] ?? control['aria-describedby'],
      'aria-invalid': existing['aria-invalid'] ?? control['aria-invalid'],
    }),
    forId: own ?? control.id,
  };
}
